import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import SaveKitButton from "@/components/SaveKitButton";
import {
  createChannelKit,
  getChannelKit,
  getLatestChannelKit,
  getNiche,
} from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RUN_STEPS = [
  "Writing channel name candidates",
  "Drafting taglines + About page",
  "Outlining first video script",
  "Picking color palette + typography",
  "Generating banner concept A",
  "Generating banner concept B",
  "Generating channel avatar",
];

function copy(text) {
  navigator.clipboard?.writeText(text);
  toast.success("Copied");
}

async function downloadImage(url, filename) {
  // Cross-origin <a download> is ignored by browsers — so fetch the file as a
  // blob and trigger a same-origin download via createObjectURL instead.
  try {
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
  } catch (err) {
    toast.error("Couldn't download image");
  }
}

export default function ChannelKit() {
  const { id: nicheId } = useParams();
  const navigate = useNavigate();
  const [niche, setNiche] = useState(null);
  const [kit, setKit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const stepTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [n, k] = await Promise.all([
        getNiche(nicheId),
        getLatestChannelKit(nicheId).catch(() => null),
      ]);
      setNiche(n);
      setKit(k && k.status === "complete" ? k : null);
      if (k && k.status === "running") {
        // Resume polling an in-flight kit
        pollKit(k.kit_id);
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nicheId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!generating) {
      setStepIdx(0);
      if (stepTimer.current) clearInterval(stepTimer.current);
      return;
    }
    stepTimer.current = setInterval(() => {
      setStepIdx((i) => Math.min(i + 1, RUN_STEPS.length - 1));
    }, 7500);
    return () => stepTimer.current && clearInterval(stepTimer.current);
  }, [generating]);

  const pollKit = async (kitId) => {
    setGenerating(true);
    const start = Date.now();
    try {
      while (Date.now() - start < 5 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 3000));
        const k = await getChannelKit(kitId);
        if (k.status === "complete") {
          setKit(k);
          toast.success("Channel kit ready");
          return;
        }
        if (k.status === "failed") {
          toast.error(k.error || "Kit generation failed");
          return;
        }
      }
      toast.error("Kit generation timed out");
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerate = async () => {
    if (generating) return;
    if (kit && !window.confirm("Generate a new kit? The current one will be replaced in the view (older kits stay in your DB).")) {
      return;
    }
    setGenerating(true);
    try {
      const { kit_id } = await createChannelKit(nicheId);
      await pollKit(kit_id);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not start kit generation";
      toast.error(typeof msg === "string" ? msg : "Failed");
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="px-6 py-10 font-mono text-sm text-zinc-500">
        [recon] loading kit<span className="blink">_</span>
      </div>
    );
  }
  if (!niche) return null;

  return (
    <div data-testid="channel-kit-page" className="px-6 lg:px-10 py-8 max-w-[1400px] mx-auto">
      <button
        data-testid="kit-back-btn"
        onClick={() => navigate(`/niches/${nicheId}`)}
        className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500 hover:text-yellow-400 transition-colors mb-6"
      >
        ◀ Back to niche
      </button>

      <header className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-3">
            // CHANNEL KIT · {niche.name}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-white leading-[0.95] mb-3">
            Your channel, ready to ship.
          </h1>
          <p className="text-zinc-400 max-w-2xl text-sm">
            Names, banners, avatar, About-page copy and the first video's
            script — generated from your niche dossier. Use these to manually
            set up your channel on YouTube in about 20 minutes.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 lg:items-end">
          {kit && kit.status === "complete" && (
            <SaveKitButton
              kit_key={kit.kit_id}
              source_type="create_channel"
              source_id={kit.niche_id}
              name={kit?.data?.channel_names?.[0]?.name || niche?.name || kit.kit_id}
              niche={niche?.name}
              data={kit?.data}
              images={kit?.data?.images}
            />
          )}
          {kit && kit.status === "complete" && (
            <a
              data-testid="download-kit-btn"
              href={`${BACKEND_URL}/api/channel-kits/${kit.kit_id}/download`}
              className="bg-emerald-400 text-emerald-950 font-bold px-6 py-3 rounded-sm uppercase tracking-wide text-sm hover:bg-emerald-300 transition-colors text-center"
            >
              ↓ Download all (.zip)
            </a>
          )}
          <button
            data-testid="generate-kit-btn"
            onClick={handleGenerate}
            disabled={generating}
            className="bg-yellow-400 text-yellow-950 font-bold px-6 py-3 rounded-sm uppercase tracking-wide text-sm hover:bg-yellow-300 transition-colors disabled:opacity-60"
          >
            {generating ? "Generating…" : kit ? "▸ Regenerate" : "▸ Generate kit"}
          </button>
        </div>
      </header>

      {generating && (
        <div
          data-testid="kit-running"
          className="bg-zinc-950 border border-zinc-800 rounded-sm p-6 mb-10"
        >
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-yellow-400 mb-3">
            // BUILDING KIT · this takes 30-90 seconds
          </div>
          <div className="space-y-2 font-mono text-sm">
            {RUN_STEPS.map((s, i) => (
              <div
                key={s}
                className={`flex items-center gap-3 ${
                  i < stepIdx ? "text-zinc-500" : i === stepIdx ? "text-white" : "text-zinc-700"
                }`}
              >
                <span className={i <= stepIdx ? "text-yellow-400" : "text-zinc-700"}>
                  {i < stepIdx ? "✓" : i === stepIdx ? "▸" : "·"}
                </span>
                <span>{s}{i === stepIdx ? <span className="blink">_</span> : null}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {!kit && !generating && (
        <div className="border border-dashed border-zinc-800 rounded-sm p-12 text-center bg-zinc-950">
          <div className="font-mono text-yellow-400 text-xs uppercase tracking-[0.3em] mb-4">
            [ NO KIT YET ]
          </div>
          <h3 className="font-display text-2xl font-bold text-white mb-3">
            Generate your channel kit
          </h3>
          <p className="text-zinc-400 max-w-md mx-auto text-sm mb-6">
            One click. Claude writes the copy and an image model creates two
            banners + an avatar tuned to your niche's color palette.
          </p>
          <button
            onClick={handleGenerate}
            className="bg-yellow-400 text-yellow-950 font-bold px-6 py-3 rounded-sm uppercase tracking-wide text-sm hover:bg-yellow-300 transition-colors"
          >
            ▸ Generate kit
          </button>
        </div>
      )}

      {kit && <KitContent kit={kit} />}
    </div>
  );
}

function KitContent({ kit }) {
  const palette = kit.color_palette || {};
  const typography = kit.typography || {};
  const fv = kit.first_video || {};
  const provider = kit.image_provider;

  return (
    <div className="space-y-10">
      {/* Banners */}
      <Section title="Banners" hint="2048×1152 — drag into YouTube Studio → Customization → Branding">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2].map((i) => {
            const url = kit.images?.[`banner_${i}.png`];
            if (!url) return null;
            return (
              <div
                key={i}
                data-testid={`banner-${i}`}
                className="bg-zinc-950 border border-zinc-800 rounded-sm overflow-hidden"
              >
                <div className="aspect-[16/9] bg-zinc-900">
                  <img
                    src={`${BACKEND_URL}${url}`}
                    alt={`Banner ${i}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex items-center justify-between p-3 font-mono text-[11px]">
                  <span className="text-zinc-500 uppercase tracking-wider">Concept {String.fromCharCode(64 + i)}</span>
                  <button
                    onClick={() => downloadImage(`${BACKEND_URL}${url}`, `banner_concept_${i}.png`)}
                    className="text-yellow-400 hover:text-yellow-300 uppercase tracking-wider"
                  >
                    ▼ Download
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Avatar + Palette + Typography */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Section title="Avatar" inset hint="1024×1024 — channel profile pic">
          {kit.images?.["avatar.png"] && (
            <div className="space-y-3">
              <div className="aspect-square bg-zinc-900 rounded-full overflow-hidden border border-zinc-800 max-w-[220px] mx-auto">
                <img
                  src={`${BACKEND_URL}${kit.images["avatar.png"]}`}
                  alt="Avatar"
                  className="w-full h-full object-cover"
                  data-testid="avatar-image"
                />
              </div>
              <button
                onClick={() => downloadImage(`${BACKEND_URL}${kit.images["avatar.png"]}`, "avatar.png")}
                className="w-full font-mono text-xs uppercase tracking-wider px-3 py-2 border border-zinc-800 text-yellow-400 hover:border-yellow-400 rounded-sm"
              >
                ▼ Download avatar
              </button>
            </div>
          )}
        </Section>

        <Section title="Color palette" inset>
          <div className="space-y-2">
            {["primary", "secondary", "accent", "background"].map((k) => (
              <div key={k} className="flex items-center gap-3">
                <div
                  className="h-8 w-8 rounded-sm border border-zinc-800 shrink-0"
                  style={{ background: palette[k] || "#000" }}
                />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">
                    {k}
                  </div>
                  <button
                    onClick={() => copy(palette[k] || "")}
                    className="font-mono text-sm text-white hover:text-yellow-400"
                  >
                    {palette[k] || "—"}
                  </button>
                </div>
              </div>
            ))}
          </div>
          {palette.reasoning && (
            <p className="text-zinc-400 text-xs mt-3 leading-relaxed">
              {palette.reasoning}
            </p>
          )}
        </Section>

        <Section title="Typography" inset>
          <div className="space-y-3">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Headline</div>
              <div className="text-white text-2xl">{typography.headline_font || "—"}</div>
            </div>
            <div>
              <div className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Body</div>
              <div className="text-white text-base">{typography.body_font || "—"}</div>
            </div>
            {typography.reasoning && (
              <p className="text-zinc-400 text-xs leading-relaxed">
                {typography.reasoning}
              </p>
            )}
            <a
              href={`https://fonts.google.com/?query=${encodeURIComponent(
                (typography.headline_font || "") + " " + (typography.body_font || ""),
              )}`}
              target="_blank"
              rel="noreferrer"
              className="block font-mono text-[11px] uppercase tracking-wider text-yellow-400 hover:text-yellow-300 mt-2"
            >
              ▸ Open Google Fonts
            </a>
          </div>
        </Section>
      </div>

      {/* Channel names */}
      <Section title="Channel name candidates" hint="Tap any name to copy. Pick the one with an available @handle on YouTube.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(kit.channel_names || []).map((cn, i) => (
            <button
              key={i}
              data-testid={`channel-name-${i}`}
              onClick={() => copy(`${cn.name}\n${cn.handle}`)}
              className="text-left bg-zinc-950 border border-zinc-800 hover:border-yellow-400 rounded-sm p-4 transition-colors"
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-display text-xl font-bold text-white">{cn.name}</span>
                <span className="font-mono text-yellow-400 text-sm">{cn.handle}</span>
              </div>
              <p className="text-zinc-400 text-xs mt-1 leading-relaxed">{cn.why}</p>
            </button>
          ))}
        </div>
      </Section>

      {/* Taglines */}
      <Section title="Tagline options">
        <div className="space-y-2">
          {(kit.taglines || []).map((t, i) => (
            <button
              key={i}
              onClick={() => copy(t)}
              className="block w-full text-left bg-zinc-950 border border-zinc-800 hover:border-yellow-400 rounded-sm px-4 py-3 transition-colors text-zinc-200"
            >
              {t}
            </button>
          ))}
        </div>
      </Section>

      {/* About page */}
      <Section
        title="About-page copy"
        hint="Paste into YouTube Studio → Customization → Basic info → Description"
        actions={
          <button
            onClick={() => copy(kit.about_page || "")}
            className="font-mono text-[11px] uppercase tracking-wider px-3 py-1.5 border border-yellow-400/60 text-yellow-400 hover:bg-yellow-400/10 rounded-sm"
          >
            Copy
          </button>
        }
      >
        <p className="text-zinc-200 leading-relaxed whitespace-pre-line">
          {kit.about_page || "—"}
        </p>
      </Section>

      {/* First video */}
      <Section title={`First video — ${fv.title || "draft"}`} hint={fv.length_target_min ? `Target length: ${fv.length_target_min} minutes` : null}>
        <div className="space-y-4">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-wider text-yellow-400 mb-2">Hook</div>
            <p className="text-zinc-200 leading-relaxed">{fv.hook}</p>
          </div>
          <div>
            <div className="font-mono text-[11px] uppercase tracking-wider text-yellow-400 mb-2">Body beats</div>
            <ol className="space-y-2">
              {(fv.body || []).map((b, i) => (
                <li key={i} className="flex gap-4 border-l-2 border-yellow-400/40 pl-4 py-1">
                  <span className="font-mono text-xs text-yellow-400 pt-0.5">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-zinc-200 text-sm leading-relaxed">{b}</span>
                </li>
              ))}
            </ol>
          </div>
          <div>
            <div className="font-mono text-[11px] uppercase tracking-wider text-yellow-400 mb-2">Closing CTA</div>
            <p className="text-zinc-200 leading-relaxed">{fv.cta}</p>
          </div>
        </div>
      </Section>

      <div className="font-mono text-[11px] text-zinc-600 border-t border-zinc-900 pt-4">
        Kit_id ::= {kit.kit_id} · image provider ::= {provider || "n/a"} · generated{" "}
        {kit.completed_at ? new Date(kit.completed_at).toLocaleString() : "—"}
      </div>
    </div>
  );
}

function Section({ title, hint, actions, children, inset }) {
  return (
    <section
      className={
        inset
          ? "bg-zinc-950 border border-zinc-800 rounded-sm p-5"
          : ""
      }
    >
      <header className="flex items-baseline justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-bold text-white">{title}</h2>
          {hint && (
            <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mt-1">
              {hint}
            </p>
          )}
        </div>
        {actions}
      </header>
      {children}
    </section>
  );
}
